import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';

export const useProspects = (options = {}) => {
  const { autoLoad = true, filters = {} } = options;
  const filtersRef = useRef(filters);

  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadProspects = useCallback(async (customFilters = {}) => {
    setLoading(true);
    setError(null);
    try {
      const params = { ...filtersRef.current, ...customFilters };
      const response = await api.prospects.getAll(params);
      setProspects(response.prospects || []);
      return response.prospects;
    } catch (err) {
      const message = err.message || 'Erreur lors du chargement des prospects';
      setError(message);
      console.error('Erreur loadProspects:', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const createProspect = useCallback(async (data) => {
    setError(null);
    try {
      const response = await api.prospects.create(data);
      const newProspect = response.prospect;
      setProspects(prev => [newProspect, ...prev]);
      return { success: true, prospect: newProspect };
    } catch (err) {
      const message = err.message || 'Erreur lors de la création';
      setError(message);
      return { success: false, error: message };
    }
  }, []);

  const updateProspect = useCallback(async (id, data) => {
    setError(null);
    try {
      const response = await api.prospects.update(id, data);
      const updated = response.prospect;
      setProspects(prev => prev.map(p => p.id === id ? updated : p));
      return { success: true, prospect: updated };
    } catch (err) {
      const message = err.message || 'Erreur lors de la mise à jour';
      setError(message);
      return { success: false, error: message };
    }
  }, []);

  const deleteProspect = useCallback(async (id) => {
    setError(null);
    try {
      await api.prospects.delete(id);
      setProspects(prev => prev.filter(p => p.id !== id));
      return { success: true };
    } catch (err) {
      const message = err.message || 'Erreur lors de la suppression';
      setError(message);
      return { success: false, error: message };
    }
  }, []);

  const changerTemperature = useCallback(async (id, temperature) => {
    setError(null);
    try {
      const response = await api.prospects.changerTemperature(id, temperature);
      const updated = response.prospect;
      setProspects(prev => prev.map(p => p.id === id ? updated : p));
      return { success: true, prospect: updated };
    } catch (err) {
      const message = err.message || 'Erreur';
      setError(message);
      return { success: false, error: message };
    }
  }, []);

  const convertirEnProjet = useCallback(async (id, data = {}) => {
    setError(null);
    try {
      const response = await api.prospects.convertirEnProjet(id, data);
      const updated = response.prospect;
      setProspects(prev => prev.map(p => p.id === id ? updated : p));
      return { success: true, prospect: updated, project: response.project };
    } catch (err) {
      const message = err.message || 'Erreur';
      setError(message);
      return { success: false, error: message };
    }
  }, []);

  useEffect(() => {
    if (autoLoad) {
      loadProspects();
    }
  }, [autoLoad, loadProspects]);

  return {
    prospects,
    loading,
    error,
    loadProspects,
    createProspect,
    updateProspect,
    deleteProspect,
    changerTemperature,
    convertirEnProjet,
  };
};

export default useProspects;
